'use client';
import React, { Fragment, useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import Header from '../../components/Header';
import PaymentsTable from '../../components/PaymentsTable';
import PaymentsTableToolbar from '../../components/admin/PaymentsTableToolbar';
import { useAuth } from '../../context/AuthContext';
import {
    listarPagamentosPorAtleta,
    PagamentoInscricao,
} from '../../services/pagamentoInscricaoService';
import {
    MEUS_PEDIDOS,
    STATUS_PEDIDO_LABEL,
    STATUS_LABEL,
    formatCurrency,
} from '../../services/quadraService';
import type { PaymentsFilter } from '../../types/pagamento';


const Payments: React.FC = () => {
    const auth = useAuth();
    const navigate = useNavigate();
    const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
    const atletaId = user?.atleta?.id ?? '';

    const [activeTab, setActiveTab] = useState<'torneios' | 'quadras'>('torneios');

    // ─── Torneios tab state ─────────────────────────────────────────────────────
    const [filters, setFilters] = useState<PaymentsFilter>({});
    const [pagamentos, setPagamentos] = useState<PagamentoInscricao[]>([]);
    const [loading, setLoading] = useState(false);
    const [errorState, setErrorState] = useState(false);
    const [refresh, setRefresh] = useState(0);
    const filtersRef = useRef(filters);
    filtersRef.current = filters;

    const [atletaTorneioOptions, setAtletaTorneioOptions] = useState<Array<{ id: string; label: string }>>([]);

    // ─── Quadras tab state ──────────────────────────────────────────────────────
    const [quadrasInicio, setQuadrasInicio] = useState('');
    const [quadrasFim, setQuadrasFim] = useState('');
    const [quadrasStatus, setQuadrasStatus] = useState('');

    const { data: pedidosData, loading: loadingPedidos } = useQuery(MEUS_PEDIDOS, {
        skip: activeTab !== 'quadras',
        fetchPolicy: 'network-only',
    });

    const pedidos: any[] = pedidosData?.meusPedidosAgendamento ?? [];

    const pedidosFiltrados = pedidos.filter((p: any) => {
        if (quadrasStatus && p.status !== quadrasStatus) return false;
        if (quadrasInicio || quadrasFim) {
            const agDatas = (p.agendamentos || []).map((a: any) => a.dataAgendamento);
            if (agDatas.length === 0) return false;
            const minData = agDatas.sort()[0];
            const maxData = agDatas.sort().reverse()[0];
            if (quadrasInicio && maxData < quadrasInicio) return false;
            if (quadrasFim && minData > quadrasFim) return false;
        }
        return true;
    });

    const filterByDate = (items: PagamentoInscricao[], currentFilters: PaymentsFilter) => {
        let filtered = items;
        if (currentFilters.dataInicio) {
            const start = dayjs(currentFilters.dataInicio).startOf('day');
            filtered = filtered.filter((p) => {
                const d = dayjs(p.dataPagamento || p.criadoEm);
                return d.isValid() && (d.isAfter(start) || d.isSame(start));
            });
        }
        if (currentFilters.dataFim) {
            const end = dayjs(currentFilters.dataFim).endOf('day');
            filtered = filtered.filter((p) => {
                const d = dayjs(p.dataPagamento || p.criadoEm);
                return d.isValid() && (d.isBefore(end) || d.isSame(end));
            });
        }
        return filtered;
    };

    useEffect(() => {
        if (!atletaId) return;
        const doLoad = async () => {
            const currentFilters = filtersRef.current;
            setLoading(true);
            setErrorState(false);
            try {
                const conn = await listarPagamentosPorAtleta(atletaId, {
                    page: 0,
                    size: 50,
                    status: (currentFilters.status as any) || undefined,
                    torneioId: currentFilters.torneioId || undefined,
                });
                const items = conn.items || [];
                const torneioMap = new Map<string, string>();
                items.forEach((p) => {
                    p.inscricoes.forEach((insc) => {
                        const t = insc.categoriaTorneio.torneio;
                        if (t?.id && t?.nome && !torneioMap.has(t.id)) {
                            torneioMap.set(t.id, t.nome);
                        }
                    });
                });
                setAtletaTorneioOptions(
                    Array.from(torneioMap.entries()).map(([id, label]) => ({ id, label }))
                );
                setPagamentos(filterByDate(items, currentFilters));
            } catch (err) {
                console.error(err);
                setErrorState(true);
            } finally {
                setLoading(false);
            }
        };
        doLoad();
    }, [atletaId, refresh]);


    const handleApply = () => {
        setRefresh((v) => v + 1);
    };

    const handleReset = () => {
        setFilters({});
    };

    const fetchTorneiosAtleta = useCallback(async (_q: string) => {
        return atletaTorneioOptions;
    }, [atletaTorneioOptions]);


    return (
        <Fragment>
            <div className="main-wrapper">
                <Header />

                <div className="main-content theme-dark-bg right-chat-active">

                    <div className="middle-sidebar-bottom">
                        <div className="middle-sidebar-left">
                            <div className="middle-wrap">
                                <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                                    <div className="card-body p-4 w-100 bg-current border-0 d-flex rounded-3">
                                        <Link to="/defaultsettings" className="d-inline-block mt-2"><i className="ti-arrow-left font-sm text-white"></i></Link>
                                        <h4 className="font-xs text-white fw-600 ms-4 mb-0 mt-2">Meus pagamentos</h4>
                                    </div>
                                </div>

                                <div className="alert alert-info d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3" role="alert">
                                    <span>
                                        Esta é a visão detalhada de pagamentos. O hub Financeiro reúne resumo, extrato e reembolsos (inclui quadras) num só lugar.
                                    </span>
                                    <Link to="/athlete/financeiro" className="btn btn-sm btn-primary">
                                        Ir para o Financeiro
                                    </Link>
                                </div>

                                {/* Abas */}
                                <ul className="nav nav-tabs mb-3">
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link font-xsss fw-600 ${activeTab === 'torneios' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('torneios')}
                                        >
                                            Torneios
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            className={`nav-link font-xsss fw-600 ${activeTab === 'quadras' ? 'active' : ''}`}
                                            onClick={() => setActiveTab('quadras')}
                                        >
                                            Aluguel de Quadras
                                        </button>
                                    </li>
                                </ul>

                                {/* Aba Torneios */}
                                {activeTab === 'torneios' && (
                                    <>
                                        <PaymentsTableToolbar
                                            availableFilters={['torneio', 'status', 'data']}
                                            initialFilters={{}}
                                            value={filters}
                                            onChange={setFilters}
                                            onApply={handleApply}
                                            onReset={handleReset}
                                            fetchTorneios={fetchTorneiosAtleta}
                                        />
                                        <PaymentsTable
                                            externalPagamentos={pagamentos}
                                            externalLoading={loading}
                                            externalError={errorState}
                                            onRefetch={handleApply}
                                        />
                                    </>
                                )}

                                {/* Aba Quadras */}
                                {activeTab === 'quadras' && (
                                    <>
                                        {/* Filtros */}
                                        <div className="card shadow-xss rounded-xxl border-0 p-3 mb-3">
                                            <div className="d-flex flex-wrap align-items-end gap-3">
                                                <div>
                                                    <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">De</label>
                                                    <input type="date" className="form-control rounded-xl font-xsss" value={quadrasInicio}
                                                        onChange={e => setQuadrasInicio(e.target.value)} style={{ width: 150, height: 38 }} />
                                                </div>
                                                <div>
                                                    <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Até</label>
                                                    <input type="date" className="form-control rounded-xl font-xsss" value={quadrasFim}
                                                        onChange={e => setQuadrasFim(e.target.value)} style={{ width: 150, height: 38 }} />
                                                </div>
                                                <div>
                                                    <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Status</label>
                                                    <select className="form-select rounded-xl font-xsss" value={quadrasStatus}
                                                        onChange={e => setQuadrasStatus(e.target.value)} style={{ width: 180, height: 38 }}>
                                                        <option value="">Todos</option>
                                                        {Object.entries(STATUS_PEDIDO_LABEL).map(([key, val]) => (
                                                            <option key={key} value={key}>{val.label}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <button className="btn btn-light rounded-xl font-xssss fw-600 px-3 d-flex align-items-center justify-content-center" style={{ height: 38 }} onClick={() => { setQuadrasInicio(''); setQuadrasFim(''); setQuadrasStatus(''); }}>
                                                    Limpar
                                                </button>
                                            </div>
                                        </div>

                                        {/* Lista */}
                                        {loadingPedidos && <div className="text-center py-4"><div className="spinner-border spinner-border-sm text-primary"></div></div>}

                                        {!loadingPedidos && pedidosFiltrados.length === 0 && (
                                            <div className="card shadow-xss rounded-xxl border-0 p-4 text-center">
                                                <i className="feather-calendar font-xxl text-grey-300 d-block mb-2"></i>
                                                <p className="text-grey-500 font-xss mb-0">Nenhum aluguel encontrado.</p>
                                            </div>
                                        )}

                                        {!loadingPedidos && pedidosFiltrados.map((pedido: any) => {
                                            const st = STATUS_PEDIDO_LABEL[pedido.status] ?? { label: pedido.status, color: 'secondary' };
                                            const agendamentos = pedido.agendamentos || [];
                                            return (
                                                <div key={pedido.id} className="card shadow-xss rounded-xxl border-0 p-3 mb-2"
                                                    onClick={() => navigate(`/quadras/pedido/${pedido.id}`)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                                        <div>
                                                            <span className="fw-700 font-xsss text-grey-900">{pedido.local?.nome ?? 'Local'}</span>
                                                            <span className="font-xssss text-grey-500 ms-2">
                                                                {dayjs(pedido.criadoEm).format('DD/MM/YYYY HH:mm')}
                                                            </span>
                                                        </div>
                                                        <span className={`badge bg-${st.color} font-xssss rounded-xl`}>{st.label}</span>
                                                    </div>
                                                    <div className="font-xssss text-grey-600">
                                                        {agendamentos.map((ag: any) => {
                                                            const agSt = STATUS_LABEL[ag.status] ?? { label: ag.status, color: 'secondary' };
                                                            return (
                                                                <div key={ag.id} className="d-flex justify-content-between py-1 border-bottom">
                                                                    <span>
                                                                        📅 {ag.dataAgendamento} · {ag.horaInicio}-{ag.horaFim} · {ag.quadra?.nome}
                                                                    </span>
                                                                    <span className={`badge bg-${agSt.color} font-xssss rounded-xl`}>{agSt.label}</span>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                    <div className="d-flex justify-content-end mt-2">
                                                        <span className="fw-700 font-xsss text-grey-900">{formatCurrency(pedido.valorTotal)}</span>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Payments
